import { api } from '@/api'
import type { UnifiedResource } from './resourceDetail.types'
import type { ResourceDeclaredSources, ResourceInvocationPlan, ResourceParameterBinding, ResourceSchemaMetadata } from '@/types/dag.types'
import type { ModelModality } from './model-capabilities'
import type { GovernanceResource } from './governanceResource.types'
export type { UnifiedResource, SharePointSelectionAuthority, SharePointSelectionItem, SharePointSelectionContributor } from './resourceDetail.types'
import { INCLUDE_SETUP } from './connectionSetupStatus'
import type { ServedHealth } from './connectionHealth'
import { normalizeResourceParameterReason, type ResourceParameterReason } from './resourceParameterReason'
import type { ExecutionMode } from '@/types/mission-unified.types'
import { withInvocationPlanWireFields } from '@/utils/invocation-plan-wire'

export type { ModelModality } from './model-capabilities'
export type { GovernanceResource } from './governanceResource.types'

// Entities that block deletion of a resource. `agent_source` ids address
// agion.agent_sources rows; `child_resource` ids address child governance
// resources. Both must be removed (or cascaded) before the parent deletes.
export type ResourceDependencyType = 'agent_source' | 'child_resource'

export interface ResourceDependency {
  type: ResourceDependencyType
  id: string
  name: string
}

// GET governance/resources/{id}/dependencies. Wire format is snake_case;
// api.get transforms resource_id/resource_name to camelCase.
export interface ResourceDependencies {
  resourceId: string
  resourceName: string
  dependencies: ResourceDependency[]
  count: number
}

import type { ResourceAnonymizedSampleRequest, ResourceAnonymizedSampleEnvelope } from './ingestion-preview-types'
export type { ResourceAnonymizedSampleRequest, ResourceAnonymizedSampleEnvelope } from './ingestion-preview-types'

export interface ResourceConnectionActionResponse {
  success: boolean
  result_kind: string
  execution_locus: string
  message: string
  details?: Record<string, unknown>
}

// AI Model from governance resources
export interface GovernanceAIModel {
  id: string
  name: string
  description?: string
  resourceType: string
  status: string
  modelIdentifier: string
  providerName: string
  providerType: string
  modality: string
  contextWindow?: number
  maxOutputTokens?: number
  supportsFunctions: boolean
  supportsStreaming: boolean
  supportsVision: boolean
  isAvailable: boolean
  createdAt: string
  updatedAt: string
}

export type ModelLifecycleState = 'active' | 'deprecated' | 'retired' | 'needs_migration'

// Types based on backend models
export interface ModelResource {
  uri: string
  modelId?: string
  /** Provider catalog format, when the discovery API exposes one. */
  modelFormat?: string
  /** Selected or discovered provider catalog version, when available. */
  modelVersion?: string
  name: string
  provider: string
  modelFamily: string
  modality?: ModelModality
  displayModality?: string
  parameters?: number
  contextSize: number
  capabilities: string[]
  pricing: {
    inputTokens: number
    outputTokens: number
    currency: string
  }
  status: 'active' | 'pending' | 'inactive'
  lifecycleState?: ModelLifecycleState
  isConfigured?: boolean
  isAvailable?: boolean
  isDeprecated?: boolean
  /**
   * Present only on a synthetic lifecycle-finding row. The URI deliberately
   * remains provider:model for navigation and backend context, while this
   * exact alert ID keeps the row distinct from a configured catalog model.
   */
  alertOnly?: {
    availabilityAlertId: string
  }
  endpoint?: string
  rateLimit?: string
  lastUsed?: string
  createdAt: string
  updatedAt: string
}

export interface PromptTemplate {
  uri: string
  name: string
  category: string
  template: string
  variables: string[]
  tags?: string[]
  framework?: string
  trustLevel: number
  status: 'active' | 'pending' | 'approved' | 'under_review'
  usageCount: number
  examples?: Array<{
    input: Record<string, any>
    output: string
  }>
  approvedBy?: string
  lastModified: string
  createdAt: string
  aiModel?: string
  agent?: string
  agentId?: string
  content?: string  // Alternative field for template content
}

export interface DataSource {
  uri: string
  name: string
  type: 'database' | 'api' | 'file' | 'stream'
  format: string
  size: number
  records: number
  schema?: any
  storageUrl?: string
  compliance: string[]
  status: 'available' | 'processing' | 'restricted' | 'offline'
  lastAccessed?: string
  createdAt: string
  updatedAt: string
}


// Backend Resource interface matching Go model JSON tags
export interface Resource {
  id: string
  organization_id: string
  uri: string
  name: string
  description?: string
  resource_type: string  // snake_case to match backend JSON
  resource_subtype?: string
  protocol?: string
  credential_id?: string
  connection_config?: Record<string, any>
  endpoint?: string
  data_region: string
  compliance_tags?: string[]
  capabilities?: {
    read: boolean
    write: boolean
    execute: boolean
    stream: boolean
    batch?: boolean
  }
  version?: string
  provider?: string
  status: 'active' | 'pending' | 'error' | 'disabled'
  /** governance-service's served health (services/connectionHealth); absent when it could not read the hosts' reports. */
  health?: ServedHealth
  health_check_interval?: number
  error_message?: string
  schema_discovered?: boolean
  schema_last_updated?: string
  schema_discovery_status?: string
  usage_count?: number
  last_accessed_at?: string
  created_by: string
  created_at: string  // snake_case to match backend JSON
  updated_at: string  // snake_case to match backend JSON

  // Camel case alternatives and object forms
  connectionMethod?: string | ConnectionMethod
  type?: string
  subtype?: string
  tags?: string[]
  metadata?: Record<string, any>
}

export interface ConnectionMethod {
  type: string
  endpoint?: string
  authentication?: 'api-key' | 'oauth' | 'basic' | 'certificate' | 'none'
  configuration?: Record<string, any>
}

export interface ResourceQuery {
  type?: 'model' | 'prompt' | 'dataset' | 'tool'
  namespace?: string
  tags?: string[]
  owner?: string
  limit?: number
  offset?: number
}

export interface ListResponse<T> {
  resources: T[]
  count: number
  limit: number
  offset: number
  total: number
}

// A refresh reindexes the spec on the host, which may take the host's full
// 30 seconds; the server allows it 50, so the page waits past that
// (Agion#2042). Not retried: a refresh is not idempotent in cost.
export const CONNECTION_REFRESH_TIMEOUT_MS = 60_000

export class ResourceService {
  constructor() {
    // Use the registry service endpoint (unified registry for all resources)
    // API_CONFIG is used by the unified API client
  }

  // Models - NOTE: There is no global /v1/models endpoint.
  // Models are fetched per-provider via /v1/providers/{id}/models
  // Use providerService.listModels(providerId) instead.
  async listModels(): Promise<ModelResource[]> {
    console.warn('[resourceService] listModels() is deprecated - use providerService.listModels(providerId)')
    return []
  }

  async getModels(): Promise<ModelResource[]> {
    return this.listModels()
  }

  async getModel(_uri: string): Promise<ModelResource> {
    throw new Error('getModel() is not supported - models are fetched per-provider via providerService')
  }

  async registerModel(_model: Partial<ModelResource>): Promise<ModelResource> {
    throw new Error('registerModel() is not supported - models are managed by providers')
  }

  async findModelsByCapability(_capability: string): Promise<ModelResource[]> {
    console.warn('[resourceService] findModelsByCapability() is deprecated')
    return []
  }

  async getModelCost(_uri: string): Promise<{ inputCost: number; outputCost: number }> {
    throw new Error('getModelCost() is not supported - use provider-specific pricing')
  }

  // Data Sources
  async listDataSources(): Promise<DataSource[]> {
    // Note: X-Agent-DID header would need to be added to the API client if required
    return await api.get<DataSource[]>('data')
  }

  async getDataSource(uri: string, _agentDID: string): Promise<DataSource> {
    // Note: X-Agent-DID header would need to be added to the API client if required
    return await api.get<DataSource>(`data/${encodeURIComponent(uri)}`)
  }

  async queryDataSource(uri: string, query: string, _agentDID: string): Promise<any> {
    // Note: X-Agent-DID header would need to be added to the API client if required
    return await api.post<any, { query: string }>(`data/${encodeURIComponent(uri)}/query`, { query })
  }

  // Unified Resources - governance/resources endpoint
  async listConnectionsPage(params: Record<string, string>): Promise<{ resources: Resource[]; total: number; limit: number; offset: number; nest?: string }> {
    const page = await api.get<{ resources: Resource[]; total: number; limit: number; offset: number; nest?: string }>('governance/resources', params)
    if (!page || !Array.isArray(page.resources) || !Number.isSafeInteger(page.total) || page.total < 0 || page.limit !== Number(params.limit) || page.offset !== Number(params.offset) || page.resources.length > page.limit) {
      throw new Error('The connection catalogue did not return a bounded page')
    }
    return page
  }

  // Without a host, the same exact-name read covers the whole organisation (Agion#2381).
  async listResourcePage(hostId: string | undefined, search: string, offset = 0, limit = 25): Promise<{ resources: UnifiedResource[]; total: number; limit: number; offset: number }> {
    const page = await api.get<{ resources: UnifiedResource[]; total: number; limit: number; offset: number }>(
      'governance/resources', { ...(hostId ? { host_id: hostId } : {}), name: search, status: 'active', offset: String(offset), limit: String(limit) },
    )
    if (!page || !Array.isArray(page.resources) || !Number.isInteger(page.total) || page.total < 0 || page.limit !== limit || page.offset !== offset || page.resources.length > limit) {
      throw new Error('The resource catalogue did not return a bounded page')
    }
    return page
  }

  async listResources(subtype?: string, hostId?: string): Promise<UnifiedResource[]> {
    const searchParams: Record<string, string> = {}
    if (subtype) searchParams.subtype = subtype
    if (hostId) searchParams.host_id = hostId
    const data = await api.get<{ resources?: UnifiedResource[] } | UnifiedResource[]>('governance/resources', Object.keys(searchParams).length ? searchParams : undefined)
    return Array.isArray(data) ? data : (data.resources || [])
  }

  // List AI models from governance resources
  async listAIModels(): Promise<GovernanceAIModel[]> {
    const data = await api.get<{ resources?: GovernanceResource[] } | GovernanceResource[]>(
      'governance/resources',
      { resource_type: 'ai_model' }
    )
    const resources = Array.isArray(data) ? data : (data.resources || [])

    // Transform to GovernanceAIModel format
    return resources.map(r => {
      const rd = r.resourceData || {}
      return {
        id: r.id,
        name: r.name,
        description: r.description,
        resourceType: r.resourceType,
        status: r.status,
        // Extract model-specific data from resourceData
        modelIdentifier: rd['modelIdentifier'] || rd['model_identifier'] || r.name,
        providerName: rd['providerName'] || rd['provider_name'] || 'Unknown',
        providerType: rd['providerType'] || rd['provider_type'] || 'unknown',
        modality: rd['modality'] || rd['primaryModality'] || 'chat',
        contextWindow: rd['contextWindow'] || rd['context_window'],
        maxOutputTokens: rd['maxOutputTokens'] || rd['max_output_tokens'],
        supportsFunctions: rd['supportsFunctions'] ?? rd['supports_functions'] ?? false,
        supportsStreaming: rd['supportsStreaming'] ?? rd['supports_streaming'] ?? true,
        supportsVision: rd['supportsVision'] ?? rd['supports_vision'] ?? false,
        isAvailable: r.status === 'active',
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }
    })
  }

  /** includeSetup asks for the connection's setup facts and status (setup_status, Agion#2673). */
  async getResource(uri: string, options: { includeSetup?: boolean } = {}): Promise<UnifiedResource> {
    const path = `governance/resources/${encodeURIComponent(uri)}`
    return options.includeSetup
      ? await api.get<UnifiedResource>(path, INCLUDE_SETUP)
      : await api.get<UnifiedResource>(path)
  }

  async getResourceChildren(uri: string): Promise<GovernanceResource[]> {
    const data = await api.get<{ children?: GovernanceResource[] }>(
      `governance/resources/${encodeURIComponent(uri)}/children`
    )
    return data.children || []
  }

  async getResourceWithCredentials(uri: string): Promise<UnifiedResource> {
    return await api.get<UnifiedResource>(
      `governance/resources/${encodeURIComponent(uri)}/credentials`
    )
  }

  async createResource(resource: Partial<UnifiedResource>): Promise<UnifiedResource> {
    return await api.post<UnifiedResource, Partial<UnifiedResource>>('governance/resources', resource)
  }

  async createGovernanceResourceRaw(resource: Record<string, unknown>): Promise<GovernanceResource> {
    return await api.postRaw<GovernanceResource, Record<string, unknown>>('governance/resources', resource)
  }

  async updateResource(uri: string, updates: Partial<UnifiedResource>): Promise<void> {
    await api.put<void, Partial<UnifiedResource>>(`governance/resources/${encodeURIComponent(uri)}`, updates)
  }

  async deleteResource(uri: string): Promise<void> {
    await api.delete<void>(`governance/resources/${encodeURIComponent(uri)}`)
  }

  // Blocking dependents of a resource: the same computation the delete
  // conflict runs, exposed before the user confirms. Deletion returns 409
  // while any of these exist.
  async getResourceDependencies(uri: string): Promise<ResourceDependencies> {
    return await api.get<ResourceDependencies>(
      `governance/resources/${encodeURIComponent(uri)}/dependencies`
    )
  }

  async refreshResourceConnection(uri: string): Promise<ResourceConnectionActionResponse> {
    return await api.post<ResourceConnectionActionResponse, Record<string, never>>(
      `governance/resources/${encodeURIComponent(uri)}/refresh`,
      {},
      { timeout: CONNECTION_REFRESH_TIMEOUT_MS, retry: 0 }
    )
  }

  // Resource Discovery
  async discoverResources(type?: string): Promise<{
    models: ModelResource[]
    prompts: PromptTemplate[]
    data: DataSource[]
  }> {
    const searchParams = type ? { type } : undefined
    // Note: X-Agent-DID header would need to be added to the API client if required
    return await api.get<{
      models: ModelResource[]
      prompts: PromptTemplate[]
      data: DataSource[]
    }>('resources/discover', searchParams)
  }

  // Get field definitions for a resource (for constraint targets)
  async getResourceFields(resourceId: string): Promise<ResourceFieldDefinition[]> {
    try {
      const response = await api.get<{
        fields: ResourceFieldDefinition[]
        field_count: number
        resource_id: string
        resource_type: string
      }>(`governance/resources/${encodeURIComponent(resourceId)}/fields`)
      return response.fields || []
    } catch (error) {
      console.warn(`Failed to fetch fields for resource ${resourceId}:`, error)
      return []
    }
  }

  // Get API endpoints for an API-type resource from BYOH host
  // This queries the host's local OpenAPI index for data residency compliance
  async getResourceApiEndpoints(resourceId: string, hostId: string): Promise<APIEndpointsResponse> {
    const response = await api.get<APIEndpointsResponse>(
      `governance/resources/${encodeURIComponent(resourceId)}/api-endpoints`,
      { host_id: hostId }
    )
    return {
      endpoints: response.endpoints || [],
      resource_id: response.resource_id || resourceId,
      spec_title: response.spec_title,
      total: response.total
    }
  }

  async getResourceParameterPlan(resourceId: string, operationId: string, hostId: string | undefined, facts: ResourceStepFacts): Promise<ResourceParameterPlanResponse | null> {
    try {
      const params: Record<string, string> = { execution_mode: facts.executionMode }
      if (hostId) {
        params.host_id = hostId
      }
      if (facts.topLevel !== undefined) params.top_level = String(facts.topLevel)
      const response = await api.get<ResourceParameterPlanResponse & Record<string, unknown>>(
        `governance/resources/${encodeURIComponent(resourceId)}/operations/${encodeURIComponent(operationId)}/parameter-plan`,
        params
      )
      return normalizeResourceParameterPlanResponse(response, resourceId, operationId)
    } catch (error) {
      console.warn(`Failed to fetch parameter plan for resource ${resourceId} operation ${operationId}:`, error)
      return null
    }
  }

  // declaredSources are the schemas the plan's sources declare (wire
  // declared_sources, agion/Agion#2466): a list bound whole from a declared
  // array-of-object output satisfies the item fields that schema carries.
  // facts are the step's mode and position (agion/Agion#2668); the mode is
  // always sent, and the server refuses a check without one.
  async validateResourceParameterPlan(resourceId: string, operationId: string, plan: ResourceInvocationPlan, hostId: string | undefined, declaredSources: ResourceDeclaredSources | undefined, facts: ResourceStepFacts): Promise<ResourceParameterPlanResponse | null> {
    try {
      const response = await api.post<ResourceParameterPlanResponse & Record<string, unknown>>(
        `governance/resources/${encodeURIComponent(resourceId)}/operations/${encodeURIComponent(operationId)}/parameter-plan/validate${hostId ? `?host_id=${encodeURIComponent(hostId)}` : ''}`,
        {
          plan,
          ...(declaredSources ? { declaredSources } : {}),
          execution_mode: facts.executionMode,
          ...(facts.topLevel !== undefined ? { top_level: facts.topLevel } : {}),
        }
      )
      return normalizeResourceParameterPlanResponse(response, resourceId, operationId)
    } catch (error) {
      console.warn(`Failed to validate parameter plan for resource ${resourceId} operation ${operationId}:`, error)
      return null
    }
  }

  // Get database schema metadata for a database resource from the BYOH host.
  // Governance resolves the bound host when hostId is omitted.
  async getResourceSchema(
    resourceId: string,
    hostId?: string,
    options?: { maxTables?: number; maxColumns?: number }
  ): Promise<ResourceSchemaMetadata | null> {
    try {
      const params: Record<string, string | number> = {
        max_tables: options?.maxTables ?? 200,
        max_columns: options?.maxColumns ?? 80,
      }
      if (hostId) {
        params.host_id = hostId
      }
      const response = await api.get<ResourceSchemaMetadata>(
        `governance/resources/${encodeURIComponent(resourceId)}/schema`,
        params
      )
      return {
        ...response,
        resource_id: response.resource_id || resourceId,
        tables: Array.isArray(response.tables) ? response.tables : [],
      }
    } catch (error) {
      console.warn(`Failed to fetch schema for resource ${resourceId}:`, error)
      return null
    }
  }

  // Fetches an anonymized column sample from the edge host via the existing
  // ingestion preview proxy: governance → intelligence-runtime → ingestion-service.
  // Raw source values are masked on-host; nothing leaves the BYOH boundary.
  async getResourceAnonymizedSample(
    resourceId: string,
    hostId: string,
    input: ResourceAnonymizedSampleRequest,
  ): Promise<ResourceAnonymizedSampleEnvelope> {
    if (!resourceId) throw new Error('resourceId is required')
    if (!hostId) throw new Error('hostId is required')
    return api.post<ResourceAnonymizedSampleEnvelope>(
      `governance/intelligence/hosts/${encodeURIComponent(hostId)}/ingestion/preview`,
      { ...input, resource_id: resourceId },
      { timeout: 90000, retry: 0 },
    )
  }

}

// Resource field definition from GET /governance/resources/:id/fields
export interface ResourceFieldDefinition {
  field_name: string
  field_type: string
  data_type?: string
  description?: string
  required?: boolean
  sample_values?: string[]
}

// API Endpoint from OpenAPI spec (returned by host query)
export interface APIEndpoint {
  method: string
  path: string
  operation_id?: string
  operationId?: string
  summary?: string
  description?: string
  tags?: string[]
  path_params?: Array<Record<string, unknown>>
  pathParams?: Array<Record<string, unknown>>
  query_params?: Array<Record<string, unknown>>
  queryParams?: Array<Record<string, unknown>>
  request_body_required?: boolean
  requestBodyRequired?: boolean
  request_body_content_types?: string[]
  requestBodyContentTypes?: string[]
  operation_contract?: Record<string, unknown>
  operationContract?: Record<string, unknown>
}

// API Endpoints response from host query
export interface APIEndpointsResponse {
  endpoints: APIEndpoint[]
  resource_id: string
  spec_title?: string
  total: number
}

export interface ResourceParameterOption {
  label: string
  value: unknown
  id?: string
  description?: string
  metadata?: Record<string, unknown>
}

export interface ResourceParameterViewModel {
  target: string
  label: string
  description?: string
  location: string
  type?: string
  required?: boolean
  required_reason?: string
  control?: 'input' | 'textarea' | 'select' | 'radio' | 'resource_picker' | 'toggle' | 'number'
  options?: ResourceParameterOption[]
  source_options?: Array<'fixed' | 'mission_input' | 'previous_output' | 'resource_option' | 'computed'>
  current_binding?: ResourceParameterBinding
  status?: 'complete' | 'missing' | 'invalid' | 'stale'
  /**
   * The platform supplies this input for each run (wire platform_owned,
   * Go resourceplan.PlatformOwnedTarget): the author need not bind it, and
   * an author binding still wins (agion/Agion#2401).
   */
  platformOwned?: boolean
  /**
   * The connection option the author picks this input within (wire
   * connection_option_id, Go resourceplan.SelectionChosen), such as
   * selected_drives for a SharePoint library (agion/Agion#2535).
   */
  connectionOptionId?: string
  /**
   * The connection sets this input and nobody else may (wire host_supplied,
   * Go resourceplan.HostSupplied), such as the SharePoint site.
   */
  hostSupplied?: boolean
  /** Why the row looks the way it does (wire reason, Go resourceplan.ParameterReason). */
  reason?: ResourceParameterReason
}

/**
 * Facts about a step the parameter-plan check judges it by (wire
 * execution_mode and top_level, agion/Agion#2668): the mission's execution
 * mode as stored, and whether the step sits at the top level of the mission
 * rather than inside an agent's workflow. Go decides from them who supplies
 * the step's arguments. The mode is required: the server refuses a check
 * without one (execution_mode_required).
 */
export interface ResourceStepFacts {
  executionMode: ExecutionMode
  topLevel?: boolean
}

export interface ResourceParameterPlanResponse {
  resource_id: string
  operation_id: string
  operation_contract_digest?: string
  operation_contract?: Record<string, unknown>
  plan?: ResourceInvocationPlan
  parameters: ResourceParameterViewModel[]
  validation?: ResourceInvocationPlan['validation']
}

function normalizeResourceParameterPlanResponse(
  response: ResourceParameterPlanResponse & Record<string, unknown>,
  resourceId: string,
  operationId: string,
): ResourceParameterPlanResponse {
  const parameters = Array.isArray(response.parameters)
    ? response.parameters.map((parameter) => normalizeResourceParameterViewModel(parameter as ResourceParameterViewModel & Record<string, unknown>))
    : [];
  const plan = normalizeInvocationPlan(response.plan as (ResourceInvocationPlan & Record<string, unknown>) | undefined)
  const validation = normalizeInvocationValidation(response.validation as Record<string, unknown> | undefined)
  return {
    ...response,
    resource_id: String(response.resource_id || response.resourceId || resourceId),
    operation_id: String(response.operation_id || response.operationId || operationId),
    operation_contract_digest: typeof response.operation_contract_digest === 'string'
      ? response.operation_contract_digest
      : typeof response.operationContractDigest === 'string'
        ? response.operationContractDigest
        : undefined,
    operation_contract: response.operation_contract && typeof response.operation_contract === 'object'
      ? response.operation_contract as Record<string, unknown>
      : response.operationContract && typeof response.operationContract === 'object'
        ? response.operationContract as Record<string, unknown>
        : undefined,
    plan,
    parameters,
    validation,
  };
}

function normalizeResourceParameterViewModel(parameter: ResourceParameterViewModel & Record<string, unknown>): ResourceParameterViewModel {
  return {
    ...parameter,
    required_reason: typeof parameter.required_reason === 'string'
      ? parameter.required_reason
      : typeof parameter.requiredReason === 'string'
        ? parameter.requiredReason
        : undefined,
    source_options: Array.isArray(parameter.source_options)
      ? parameter.source_options
      : Array.isArray(parameter.sourceOptions)
        ? parameter.sourceOptions as ResourceParameterViewModel['source_options']
        : undefined,
    current_binding: normalizeInvocationBinding(parameter.current_binding as Record<string, unknown> | undefined)
      || normalizeInvocationBinding(parameter.currentBinding as Record<string, unknown> | undefined),
    platformOwned: parameter.platformOwned === true || parameter.platform_owned === true,
    connectionOptionId: typeof parameter.connectionOptionId === 'string' && parameter.connectionOptionId
      ? parameter.connectionOptionId
      : typeof parameter.connection_option_id === 'string' && parameter.connection_option_id
        ? parameter.connection_option_id
        : undefined,
    hostSupplied: parameter.hostSupplied === true || parameter.host_supplied === true,
    reason: normalizeResourceParameterReason(parameter.reason),
  }
}

function normalizeInvocationPlan(plan?: ResourceInvocationPlan & Record<string, unknown>): ResourceInvocationPlan | undefined {
  if (!plan) return undefined
  return {
    ...withInvocationPlanWireFields(plan),
    bindings: Array.isArray(plan.bindings)
      ? plan.bindings.map((binding) => normalizeInvocationBinding(binding as unknown as Record<string, unknown>)).filter(Boolean) as ResourceParameterBinding[]
      : [],
    validation: normalizeInvocationValidation(plan.validation as Record<string, unknown> | undefined),
  }
}

function normalizeInvocationBinding(binding?: Record<string, unknown>): ResourceParameterBinding | undefined {
  if (!binding || typeof binding.target !== 'string' || typeof binding.source !== 'string') return undefined
  return {
    ...binding,
    target: binding.target,
    source: binding.source as ResourceParameterBinding['source'],
    input_key: typeof binding.input_key === 'string' ? binding.input_key : typeof binding.inputKey === 'string' ? binding.inputKey : undefined,
    output_ref: typeof binding.output_ref === 'string' ? binding.output_ref : typeof binding.outputRef === 'string' ? binding.outputRef : undefined,
    option_id: typeof binding.option_id === 'string' ? binding.option_id : typeof binding.optionId === 'string' ? binding.optionId : undefined,
  } as ResourceParameterBinding
}

// The verdict in its wire spelling only (Go shareddag.ResourceInvocationValidation):
// the designer saves it into the step as the server answered it, so the
// camelCase copies the API client adds are not carried along.
function normalizeInvocationValidation(validation?: Record<string, unknown>): ResourceInvocationPlan['validation'] | undefined {
  if (!validation) return undefined
  const list = (snake: string, camel: string): string[] | undefined => {
    const value = validation[snake] ?? validation[camel]
    return Array.isArray(value) ? value as string[] : undefined
  }
  const alternatives = validation.missing_required_alternatives ?? validation.missingRequiredAlternatives
  const result: NonNullable<ResourceInvocationPlan['validation']> = {}
  if (typeof validation.status === 'string') result.status = validation.status as NonNullable<ResourceInvocationPlan['validation']>['status']
  const missing = list('missing_required_targets', 'missingRequiredTargets')
  if (missing) result.missing_required_targets = missing
  const invalid = list('invalid_bindings', 'invalidBindings')
  if (invalid) result.invalid_bindings = invalid
  const unknown = list('unknown_targets', 'unknownTargets')
  if (unknown) result.unknown_targets = unknown
  if (Array.isArray(alternatives)) {
    result.missing_required_alternatives = (alternatives as Array<Record<string, unknown>>).map((issue) => {
      const valid = issue.valid_alternatives ?? issue.validAlternatives
      return { path: String(issue.path ?? ''), ...(Array.isArray(valid) ? { valid_alternatives: valid as string[][] } : {}) }
    })
  }
  return result
}

export const resourceService = new ResourceService()
